import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  /** @type {Promise<void> | undefined} */
  #zoomReady;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
    this.addEventListener('pointerdown', this.#hydrateDeferredImages, { signal, capture: true, passive: true });
    this.addEventListener('pointerenter', this.#hydrateDeferredImages, { signal, passive: true });
    this.addEventListener('focusin', this.#hydrateDeferredImages, { signal });
    this.addEventListener('keydown', this.#hydrateDeferredImages, { signal, capture: true });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.#hydrateDeferredImages();
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  #hydrateDeferredImages = () => {
    const deferredImages = this.querySelectorAll('img[data-oasis-deferred-src]');

    for (const image of deferredImages) {
      if (!(image instanceof HTMLImageElement)) continue;

      const srcset = image.dataset.oasisDeferredSrcset;
      const src = image.dataset.oasisDeferredSrc;

      if (srcset) image.srcset = srcset;
      if (src) image.src = src;

      image.removeAttribute('data-oasis-deferred-src');
      image.removeAttribute('data-oasis-deferred-srcset');
      image.removeAttribute('data-oasis-deferred-media');
    }
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  async zoom(index, event) {
    event.preventDefault();
    this.#hydrateDeferredImages();

    await this.#loadZoomDialog();

    const zoomDialog =
      this.refs.zoomDialogComponent ||
      /** @type {import('./zoom-dialog').ZoomDialog | undefined} */ (this.querySelector('zoom-dialog'));

    zoomDialog?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    this.#loadZoomDialog().then(() => {
      const zoomDialog =
        this.refs.zoomDialogComponent ||
        /** @type {import('./zoom-dialog').ZoomDialog | undefined} */ (this.querySelector('zoom-dialog'));

      const zoomDialogMedia = zoomDialog?.refs.media[index];
      if (!zoomDialogMedia) return;

      zoomDialog?.loadHighResolutionImage(zoomDialogMedia);
    });
  }

  async #loadZoomDialog() {
    const zoomDialogScriptSrc = this.dataset.zoomDialogScriptSrc;
    const dragZoomScriptSrc = this.dataset.dragZoomScriptSrc;
    const needsZoomDialog = Boolean(zoomDialogScriptSrc && !customElements.get('zoom-dialog'));
    const needsDragZoom = Boolean(dragZoomScriptSrc && !customElements.get('drag-zoom-wrapper'));

    if (!needsZoomDialog && !needsDragZoom) {
      this.#mountZoomDialog();
      return;
    }
    if (this.#zoomReady) return this.#zoomReady;

    this.#zoomReady = Promise.all([
      needsDragZoom && dragZoomScriptSrc ? import(dragZoomScriptSrc) : Promise.resolve(),
      needsZoomDialog && zoomDialogScriptSrc ? import(zoomDialogScriptSrc) : Promise.resolve(),
    ])
      .then(() => (needsZoomDialog ? customElements.whenDefined('zoom-dialog') : undefined))
      .then(() => {
        if (needsDragZoom) return customElements.whenDefined('drag-zoom-wrapper');
      })
      .then(() => {
        this.#mountZoomDialog();
      });

    return this.#zoomReady;
  }

  #mountZoomDialog() {
    const existing = this.querySelector('zoom-dialog');
    if (existing) return existing;

    const template = this.querySelector('template[data-zoom-dialog-template]');
    if (!(template instanceof HTMLTemplateElement)) return;

    const fragment = /** @type {DocumentFragment} */ (template.content.cloneNode(true));
    const zoomDialog = fragment.querySelector('zoom-dialog');
    if (!(zoomDialog instanceof HTMLElement)) return;

    this.#populateZoomDialog(zoomDialog);
    template.after(fragment);

    const mounted = this.querySelector('zoom-dialog');
    mounted?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal: this.#controller.signal,
    });

    return mounted;
  }

  /**
   * Builds the zoom dialog from the already-rendered gallery media so the initial
   * product HTML does not duplicate every full-size gallery item.
   *
   * @param {HTMLElement} zoomDialog
   */
  #populateZoomDialog(zoomDialog) {
    const thumbnails = zoomDialog.querySelector('[ref="thumbnails"]');
    const gallery = zoomDialog.querySelector('[ref="gallery"]');
    const slides = Array.from(this.querySelectorAll('slideshow-slide.product-media-container'));

    if (!(thumbnails instanceof HTMLElement) || !(gallery instanceof HTMLElement) || slides.length === 0) return;

    slides.forEach((slide, index) => {
      const thumbnailSource = this.querySelector(`.slideshow-controls__thumbnail[on\\:click="/select/${index}"]`);
      const thumbnail = document.createElement('button');
      thumbnail.type = 'button';
      thumbnail.className = 'button button-unstyled dialog-thumbnails-list__thumbnail';
      thumbnail.setAttribute('aria-label', thumbnailSource?.getAttribute('aria-label') || `Image ${index + 1}`);
      thumbnail.setAttribute('on:click', `/handleThumbnailClick/${index}`);
      thumbnail.setAttribute('on:pointerenter', `/handleThumbnailPointerEnter/${index}`);
      thumbnail.setAttribute('style', thumbnailSource?.getAttribute('style') || '--aspect-ratio: 1; --gallery-aspect-ratio: 1;');
      if (index === 0) thumbnail.setAttribute('aria-selected', 'true');

      const thumbnailImage = thumbnailSource?.querySelector('img')?.cloneNode(true);
      if (thumbnailImage instanceof HTMLImageElement) {
        thumbnailImage.loading = 'lazy';
        thumbnail.append(thumbnailImage);
      }

      thumbnails.append(thumbnail);

      const mediaItem = document.createElement('li');
      mediaItem.className = slide.className;
      mediaItem.setAttribute('ref', 'media[]');
      mediaItem.setAttribute('style', slide.getAttribute('style') || '');
      if (slide.dataset.focalPoint) mediaItem.dataset.focalPoint = slide.dataset.focalPoint;

      const mediaContent = slide.cloneNode(true);
      if (mediaContent instanceof HTMLElement) {
        mediaContent.removeAttribute('ref');
        mediaContent.removeAttribute('aria-hidden');
        mediaContent.removeAttribute('on:click');

        if (mediaItem.classList.contains('product-media-container--image')) {
          mediaItem.setAttribute('on:click', '/close');

          const zoomImage = mediaContent.querySelector('img.product-media__image');
          zoomImage?.setAttribute('ref', 'image');

          const dragWrapper = document.createElement('drag-zoom-wrapper');
          dragWrapper.className = 'product-media__drag-zoom-wrapper';
          dragWrapper.append(...Array.from(mediaContent.childNodes));
          mediaItem.append(dragWrapper);
        } else {
          mediaItem.append(...Array.from(mediaContent.childNodes));
        }
      }

      gallery.append(mediaItem);
    });
  }

  get zoomDialogComponent() {
    return (
      this.refs.zoomDialogComponent ||
      /** @type {import('./zoom-dialog').ZoomDialog | undefined} */ (this.querySelector('zoom-dialog'))
    );
  }

  preloadZoomImage(index) {
    const zoomDialogMedia = this.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
